import React, { useEffect, useRef, useState } from 'react';

interface TradingChartProps {
  selectedPair: string;
}

const TradingChart: React.FC<TradingChartProps> = ({ selectedPair }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<any>(null);
  const [chartLoaded, setChartLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isContainerReady, setIsContainerReady] = useState(false);

  // Ensure container is ready before loading chart
  useEffect(() => {
    if (containerRef.current) {
      // Check if container has dimensions
      const rect = containerRef.current.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setIsContainerReady(true);
      } else {
        // Wait a bit and check again
        const timeout = setTimeout(() => {
          if (containerRef.current) {
            const newRect = containerRef.current.getBoundingClientRect();
            if (newRect.width > 0 && newRect.height > 0) {
              setIsContainerReady(true);
            }
          }
        }, 100);
        
        return () => clearTimeout(timeout);
      }
    }
  }, [selectedPair]);

  useEffect(() => {
    // Reset states
    setChartLoaded(false);
    setError(null);
    
    // Skip chart creation if selectedPair is empty or container not ready
    if (!selectedPair || !isContainerReady) {
      if (!selectedPair) {
        setError("No trading pair selected");
      }
      return;
    }
    
    // Clean up previous widget
    if (widgetRef.current) {
      try {
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }
      } catch (error) {
        console.error('Error cleaning up TradingView widget:', error);
      }
    }

    if (containerRef.current) {
      // Ensure container is visible and has proper styling
      const container = containerRef.current;
      container.style.position = 'relative';
      container.style.width = '100%';
      container.style.height = '100%';
      
      // Create container div for TradingView
      const chartContainer = document.createElement('div');
      chartContainer.id = 'tradingview_chart_' + Date.now(); // Unique ID to avoid conflicts
      chartContainer.style.height = '100%';
      chartContainer.style.width = '100%';
      chartContainer.style.position = 'relative';
      
      // Ensure the chart container is properly attached to DOM
      container.appendChild(chartContainer);

      // Wait a moment to ensure DOM is ready
      setTimeout(() => {
        try {
          // Create script element
          const script = document.createElement('script');
          script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
          script.type = 'text/javascript';
          script.async = true;

          // Add onload event to track when chart is loaded
          script.onload = () => {
            console.log('TradingView script loaded successfully');
            setChartLoaded(true);
          };

          // Add error handler for script loading
          script.onerror = (error) => {
            console.error('Error loading TradingView script:', error);
            setError('Failed to load TradingView chart');
          };

          // Convert pair format for TradingView (e.g., BTCUSDT -> BYBIT:BTCUSDT)
          let tvSymbol = selectedPair;
          let exchange = 'BYBIT'; // Changed from BINANCE to BYBIT

          // Handle different pair formats
          if (selectedPair.endsWith('USDT')) {
            // For crypto pairs, use BYBIT
            tvSymbol = selectedPair;
            exchange = 'BYBIT';
          } else if (selectedPair.includes('USD') && !selectedPair.includes('USDT')) {
            // For forex/commodity pairs
            if (selectedPair.startsWith('XAU') || selectedPair.startsWith('XAG') || selectedPair.startsWith('XPT')) {
              exchange = 'OANDA'; // For commodities
            } else if (selectedPair.length === 6) {
              exchange = 'OANDA'; // For forex
            }
          } else {
            // For stocks
            exchange = 'NASDAQ';
          }

          const widgetConfig = {
            autosize: true,
            symbol: `${exchange}:${tvSymbol}`,
            interval: '1',
            timezone: 'Etc/UTC',
            theme: 'dark',
            style: '1',
            locale: 'en',
            enable_publishing: false,
            backgroundColor: 'rgba(15, 23, 42, 1)',
            gridColor: 'rgba(71, 85, 105, 0.3)',
            hide_top_toolbar: true,
            hide_legend: false,
            save_image: false,
            container_id: chartContainer.id,
            studies: [
              'Volume@tv-basicstudies'
            ],
            overrides: {
              'paneProperties.background': '#0f172a',
              'paneProperties.backgroundType': 'solid',
              'mainSeriesProperties.candleStyle.upColor': '#10b981',
              'mainSeriesProperties.candleStyle.downColor': '#ef4444',
              'mainSeriesProperties.candleStyle.borderUpColor': '#10b981',
              'mainSeriesProperties.candleStyle.borderDownColor': '#ef4444',
              'mainSeriesProperties.candleStyle.wickUpColor': '#10b981',
              'mainSeriesProperties.candleStyle.wickDownColor': '#ef4444'
            },
            // Add additional properties to help with iframe issues
            width: '100%',
            height: '100%',
            allow_symbol_change: false,
            details: false,
            hotlist: false,
            calendar: false
          };

          script.innerHTML = JSON.stringify(widgetConfig);

          // Store reference to script for cleanup
          widgetRef.current = script;
          
          // Append script to container
          chartContainer.appendChild(script);
          
        } catch (error) {
          console.error('Error creating TradingView widget:', error);
          setError('Failed to initialize TradingView chart');
        }
      }, 200); // Increased delay to ensure DOM and iframe are ready
    }

    return () => {
      // Clean up on unmount or when selectedPair changes
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      widgetRef.current = null;
      setChartLoaded(false);
      setError(null);
    };
  }, [selectedPair, isContainerReady]);

  return (
    <div className="bg-slate-900 min-h-[400px] h-[400px] relative overflow-hidden" translate="no">
      {(!chartLoaded || !isContainerReady) && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 z-10">
          {error ? (
            <div className="text-slate-400 text-center">
              <p className="mb-2">{error}</p>
              <p className="text-sm">Please select a valid trading pair</p>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-3">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-slate-400 text-sm">Loading chart...</p>
            </div>
          )}
        </div>
      )}
      <div 
        ref={containerRef} 
        className="h-full w-full min-h-[400px]"
        translate="no"
        style={{ 
          position: 'relative',
          overflow: 'hidden'
        }}
      />
    </div>
  );
};

export default TradingChart;